import React, { useCallback, useEffect, useMemo, useState } from "react";
import { filter, isEmpty, noop } from "lodash";
import { formatNumber, formatNumberWithCommas, Sorters } from "utils/tableUtils";
import FindingAPI from "restAPI/FindingAPI";
import classNames from "classnames";
import { useHistory } from "react-router-dom";
import { updateURLSearch } from "utils/urlUtil";
import WidgetCard from "components/widgets/WidgetCard";
import { WidgetEmpty } from "components/widgets/WidgetEmpty";
import ColumnFilterComparison from "components/ColumnFilterComparison";
import moment from "moment";
import useActiveSeveritiesKeys from "hooks/useActiveSeveritiesKeys";
import { InfoIcon } from "components/Icons";
import { Tooltip } from "antd";
import { CUSTOM_WIDGET_FINDING_DISPLAY_TYPE, WidgetDisplayTypeInitialValues } from "../CustomDashboardConstants";
import useFindingFiltersByGlobalFilters from "hooks/useFindingFiltersByGlobalFilters";
import { SEVERITY_NAMES } from "views/Findings/Constants";
import { RiskRatingKeys, RiskRatings } from "utils/riskScoreUtil";
import { hexToRgb } from "constants/Layout";
import ACRiskRatingTag from "ac-components/ac-risk-rating-tag/ACRiskRatingTag";
import ACSeverityTag from "ac-components/ACSeverityTag";
import { getVocabularyColumnWidth } from "utils/textUtil";
import { getVocabulary } from "components/Vocabulary";
import { getIsVisualModeForRedirect } from "utils/aqlFilterUtils";
import { useWidgetApiInterceptor } from "contexts/WidgetApiInterceptorContext";

const sortedRiskRatings = RiskRatingKeys.sort(Sorters.RISK_RATING("", true));

export const ageHeatMaphelperText = (
  <ul>
    <li>
      Found on: Shows the number of unresolved/resolved findings by age,
      depending on the selected saved search.
    </li>

    <li>
      CVE Published Date: Shows the number of unresolved/resolved findings by
      CVE published date, depending on the selected saved search.
    </li>
  </ul>
);

export default function FindingAgeHeatMap({ config, filters }) {
  const { heatmapOn, dataSource: savedSearchId, buckets = WidgetDisplayTypeInitialValues[CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.AGE_HEATMAP].buckets, groupBy = "severity" } = config;
  const [finalStats, setFinalStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const activeSeverities = useActiveSeveritiesKeys();
  const { intercept } = useWidgetApiInterceptor();
  const finalFilters = useFindingFiltersByGlobalFilters(filters);
  const { severity, riskRating } = finalFilters;

  const finalActiveSeverities = filter(
    isEmpty(severity) ? activeSeverities : severity
  , sev => sev !== SEVERITY_NAMES.info).sort(Sorters.SEVERITY("", true));

  const masterSeverities = filter(activeSeverities, s => s !== SEVERITY_NAMES.info)
  .sort(Sorters.SEVERITY("", true));

  const bucketLabels = useMemo(() => {
    const labels = buckets.map((b, i) => {
      const start = i === 0 ? 0 : buckets[i - 1] + 1;
      return `${start}-${b}`;
    });
    labels.push(`${buckets[buckets.length - 1]}+`);
    return ["Age", ...labels];
  }, [buckets]);

  const [ranges, days] = useMemo(() => {
    const ranges = [];
    const days = [];
    buckets.forEach((range, i) => {
      const rangeArray = [i === 0 ? 0 : buckets[i - 1] + 1, range];
      ranges.push(rangeArray);
      days.push([rangeArray[0], rangeArray[1]].join('-'));
    });
    ranges.push([buckets[buckets.length - 1] + 1, '']);
    days.push(`${buckets[buckets.length - 1]}+`);
    return [ranges, days];
  }, [buckets]);

  const getPromises = useCallback(() => {
    const promises = ranges.map((foundOn, i) => {
      finalFilters[heatmapOn] = getPublishedDateValues(foundOn);
      const { promise, cancel, requestInfo } = FindingAPI.getStatsAll({
        secAggField: groupBy,
        filters: {
          ...finalFilters,
        },
      });
      return { promise: intercept(promise, requestInfo), cancel };
    });
    return promises;
  }, [finalFilters, heatmapOn, ranges, groupBy, intercept]);

  useEffect(() => {
    const promiseAndCancel = getPromises();
    const promises = promiseAndCancel.map((i) => i.promise);
    const cancels = promiseAndCancel.map((i) => i.cancel);
    setLoading(true);
    Promise.all(promises)
      .then((statsArray) => {
        const results = statsArray.map((stats, i) => ({
          count: getCount(stats),
          name: days[i],
        }));
        setFinalStats(results);
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
    return () => {
      cancels.map((cancel) => cancel());
    };
  }, [days, getPromises]);

  const onCellClick = useCallback(
    (severity, range) => {
      const newfilters = {
        ...finalFilters,
        [groupBy]: [severity],
        [heatmapOn]: getPublishedDateValues(ranges[days.indexOf(range)]),
      };
      updateURLSearch(
        history,
        {
          filters: newfilters,
          pagination: {
            page: 0,
          },
          savedSearch: savedSearchId ? { id: savedSearchId } : undefined,
          isVisualMode: getIsVisualModeForRedirect(config.filterType),
        },
        "/findings"
      );
    },
    [finalFilters, heatmapOn, ranges, days, history, savedSearchId, groupBy, config.filterType]
  );

  const tableRow = useMemo(() => {
    const isRiskRating = groupBy === "riskRating";
    const finalRiskRatings = isEmpty(riskRating) ? sortedRiskRatings : riskRating.sort(Sorters.RISK_RATING("", true));
    const finalData = isRiskRating ? finalRiskRatings : finalActiveSeverities;

    return finalData.map((key) => ({
      key,
    }));
  }, [groupBy, riskRating, finalActiveSeverities]);

  const calculatedVocabularyWidth = getVocabularyColumnWidth(groupBy, 20);

  return !loading && isEmpty(finalStats) ? (
    <WidgetEmpty description="No data" imgPath="/images/noproduct.png" />
  ) : (
    <>
      <WidgetCard.Extras>
        <Tooltip title={ageHeatMaphelperText}>
          <InfoIcon />
        </Tooltip>
      </WidgetCard.Extras>
      <WidgetCard.Spinner spinning={loading}>
        <div className="age-heatmap">
          <div className="age-heatmap-table">
            <div className="age-heatmap-row" style={{ alignItems: "end" }}>
              {bucketLabels.map((label, labelIndex) => {
                return (
                  labelIndex === 0 ? 
                  (
                    <div style={{ minWidth: calculatedVocabularyWidth, width: calculatedVocabularyWidth }}>
                      <div className="text-highlight">{heatmapOn === "publishedDate" ? "CVE" : ""} Age</div>
                      <div className="text-small-highlight color-supporting">DAYS</div>
                    </div>
                  )
                  :
                  (
                    <div key={label} className="age-heatmap-cell text-default">
                      {label}
                    </div>
                  )
                )})
              }
            </div>
            {tableRow.map((row, rIdx) => {
              const fullRowIndex = groupBy === "riskRating"
                ? sortedRiskRatings.indexOf(row.key)
                : masterSeverities.indexOf(row.key);

              return (
                <div key={row.key} className="age-heatmap-row">
                  <div className="header" style={{
                      minWidth: calculatedVocabularyWidth,
                      width: calculatedVocabularyWidth,
                      textAlign: "left",
                    }}
                  >
                    {groupBy === "riskRating" ? <ACRiskRatingTag rating={row.key} /> : <ACSeverityTag value={row.key}/>}
                  </div>
                  {(bucketLabels.slice(1)).map((bucketName, cIdx) => {
                    const stat = finalStats.find((f) => f.name === bucketName);
                    const count = stat?.count[row.key];
                    const bg = getPaletteColor(
                      groupBy === "riskRating"
                        ? getRiskRatingCellColors()
                        : severitycellColors,
                      cIdx + 1,
                      fullRowIndex + 1,
                      "#E8EAED"
                    );
                    const fontColor = getPaletteColor(
                      groupBy === "riskRating"
                        ? riskRatingcellFontColors
                        : severitycellFontColors,
                      cIdx + 1,
                      fullRowIndex + 1,
                      "#000"
                    );
                    const clickable = count > 0;
                    const countDoesNotExist = !count;
                    const displayCount = countDoesNotExist ? "" : formatNumber({ count, onlyString: true, showTooltip: false }) || 0;
                    const toolTipText = countDoesNotExist ? "" : `${getVocabulary(row.key)}: ${bucketName} Days: ${formatNumberWithCommas(count)}`;
                    return (
                      <Tooltip title={toolTipText} key={bucketName}>
                      <div
                        className={classNames("age-heatmap-cell text-small-highlight", {
                          clickable,
                        })}
                        style={countDoesNotExist ? { backgroundColor: "#E8EAED" } : { backgroundColor: bg, color: fontColor }}
                        onClick={
                          clickable ? () => onCellClick(row.key, bucketName) : noop
                        }
                      >
                        {displayCount}
                      </div>
                      </Tooltip>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </WidgetCard.Spinner>
    </>
  );
}

const getCount = (stats) => {
  return stats.stats["Reported"].severity;
};

const severitycellColors = [
  ["", "", "", "", ""],
  ["", "#FAE4E3", "#FDEBE4", "#FEF2E4", "#FEFAF3"],
  ["", "#F5C9C7", "#FAD7C9", "#FCE5C9", "#FDF6E6"],
  ["", "#EDA3A0", "#F6BBA3", "#F9D3A3", "#FAEFD4"],
  ["", "#E57772", "#F29B76", "#F7BE76", "#F8E7C0"],
  ["", "#DC4942", "#EE7948", "#F5A748", "#F5DFAA"],
];

const severitycellFontColors = [
  ["", "", "", "", ""],
  ["", "#BC2C26", "#C05225", "#A54E03", "#A27200"],
  ["", "#BC2C26", "#C05225", "#A54E03", "#A27200"],
  ["", "#7A2A27", "#8D4223", "#99571D", "#876923"],
  ["", "#FFFFFF", "#FFFFFF", "#FFFFFF", "#876923"],
  ["", "#FFFFFF", "#FFFFFF", "#FFFFFF", "#876923"],
];

const getPublishedDateValues = (range) => {
  const { startDate, endDate } = ColumnFilterComparison.toDates(range);
  return [moment.utc(startDate).format(), moment.utc(endDate).format()];
};
const RISK_RATING_BACKGROUND_CELL_COLOR_MAP = {
    [RiskRatings.goodRiskScore]: "#00BF89", 
    [RiskRatings.fairRiskScore]: "#F5A748", 
    [RiskRatings.poorRiskScore]: "EE7948", 
    [RiskRatings.veryPoorRiskScore]: "#DC4942"
}

const getRiskRatingCellColors = () => {
  const opacities = [0, 0.15, 0.3, 0.5, 0.75, 1,];

  return opacities.map((opacity) => {
    return ["", ...(sortedRiskRatings).map((rating) => hexToRgb(RISK_RATING_BACKGROUND_CELL_COLOR_MAP[rating], opacity))];
  });
};

const riskRatingcellFontColors = [
  ["", "", "", "", ""],
  ["", "#BC2C26", "#C05225", "#A54E03", "#017D45"],
  ["", "#BC2C26", "#C05225", "#A54E03", "#017D45"],
  ["", "#7A2A27", "#8D4223", "#99571D", "#017D45"],
  ["", "#FFFFFF", "#FFFFFF", "#FFFFFF", "#FFFFFF"],
  ["", "#FFFFFF", "#FFFFFF", "#FFFFFF", "#FFFFFF"],
];

function getPaletteColor(
  palette,
  col,
  row,
  fallback ="transparent"
) {
  const colPalette = palette[col] || palette[palette.length - 1];
  return colPalette?.[row] ?? colPalette[colPalette.length - 1] ?? fallback;
}